import { Organization, WithContext } from 'schema-dts';
import { MarketStoryblok } from '~/types/storyblok-generated';
import { GeigerLocale } from '~/utility/i18n';

/**
 * Generates Organization JSON-LD schema for Geiger Gruppe
 * @param marketSettings Market-specific settings containing contact and address data
 * @param locale Current locale for language-specific data
 * @param baseUrl Base URL of the website
 * @returns Organization schema object
 */
export function generateOrganizationSchema(
  marketSettings: MarketStoryblok,
  locale: GeigerLocale
): WithContext<Organization> {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Geiger Gruppe',
    url: 'https://www.geigergruppe.com',
    logo: 'https://www.geigergruppe.com/img/logo.svg',
    description: getOrganizationDescription(locale),
    foundingDate: '1923',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Wilhelm-Geiger-Straße 1',
      addressLocality: 'Oberstdorf',
      postalCode: '87561',
      addressCountry: 'DE',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: marketSettings.phoneNumber,
      email: marketSettings.emailAddress,
      contactType: 'customer service',
    },
    sameAs: marketSettings.socialLinks?.map(link => link.link.url),
  };
}

/**
 * Get organization description based on locale
 */
function getOrganizationDescription(locale: GeigerLocale): string {
  const descriptions: Record<GeigerLocale, string> = {
    [GeigerLocale.DE_DE]:
      'Die Geiger Gruppe ist ein führendes Familienunternehmen im Bereich nachhaltiges Bauen und Kreislaufwirtschaft.',
    [GeigerLocale.AT_DE]:
      'Die Geiger Gruppe ist ein führendes Familienunternehmen im Bereich nachhaltiges Bauen und Kreislaufwirtschaft.',
    [GeigerLocale.IT_IT]:
      "Il Gruppo Geiger è un'azienda familiare leader nel settore dell'edilizia sostenibile e dell'economia circolare.",
    [GeigerLocale.RO_RO]:
      'Grupul Geiger este o companie de familie lider în domeniul construcțiilor durabile și economiei circulare.',
  };
  return descriptions[locale];
}
